'use client';

import { useRouter } from '@/react/utils/router-utils/useRouter';
import { enableStaticRendering } from 'mobx-react-lite';
import { usePathname } from 'next/navigation';
import NextTopLoader from 'nextjs-toploader';
import { Suspense, useMemo, type FC, type PropsWithChildren } from 'react';

// Added to resolve Logger error when using in the browser: https://github.com/winstonjs/winston/issues/1354#issuecomment-546271943
import { Datadog } from '@/react/components/Datadog';

import Forter from '@/react/components/fraud/Forter';
import DynamicYield from '@/react/components/personalization/DynamicYield';
import GTM from '@/react/components/user-action/GTM';
import AttentiveMarketing from '@/react/components/user-action/AttentiveMarketing';
import { Preview } from '@/react/components/dev/Preview';

import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import {
  ReactHooksWrapper,
  setHook
} from '@/services/utils/react-utils/hook-utils';

import { GlobalProvider } from '@/react/providers/global/GlobalProvider';
import { OrderCancellationProvider } from '@/react/providers/order-cancellation/OrderCancellationProvider';
import { ReturnsFlowProvider } from '@/react/providers/returns-flow/ReturnsFlowProvider';

import { BreakpointSizeProvider } from '@/react/components/core-ui/Breakpoints';
import { GlobalErrorAlertProvider } from '@/react/components/core-ui/ErrorAlert/GlobalErrorAlertProvider';
import { RootErrorBoundary } from '@/react/components/errors/boundaries/RootErrorBoundary';
import { TopErrorAlertList } from '@/react/components/errors/devtools/TopErrorAlertList';
import { PageProvider } from '@/react/components/templates/context/PageProvider';
import { NotificationsManager } from '@/react/components/utils/NotificationsManager';
import { RouterEventProvider } from '@/react/providers/router-events/RouterEventProvider';
import { RouterEvents } from '@/react/providers/router-events/RouterEvents';
import { PageService } from '@/services/isomorphic/PageService';
import {
  headerLoadingBarColor,
  headerLoadingBarHeight
} from '@/styles/variables.module.scss';
import { InvalidStateError } from '@/utils/errors';
import { isNullish } from '@/utils/null-utils';

// Prevent MobX from leaking memory when rendering server-side
enableStaticRendering((typeof window === "undefined"));

export interface IClientRootProps {
  /**
   * The children of the ClientRoot component.
   */
  children: React.ReactNode;
}

/** This is the top-most Client Component. */
const ClientRoot: React.FC<IClientRootProps> = ({ children }) => {
  // Although this is deprecated, we don't have a better way to access the router
  // state in a deep arbitrary context on the client.
  setHook('Router', useRouter);

  return (
    <RouterEventProvider>
      <Suspense fallback={null}>
        <RouterEvents />
      </Suspense>
      <BreakpointSizeProvider>
        <TopErrorAlertList />
        <RootErrorBoundary>
          <GlobalErrorAlertProvider>
            <NextTopLoader
              color={headerLoadingBarColor}
              height={parseInt(headerLoadingBarHeight, 10)}
              showSpinner={false}
            />
            <PageFromPathProvider>
              <NotificationsManager>
                <GlobalProvider>
                  <GTM />
                  <AttentiveMarketing />
                  <Forter />
                  <Datadog />
                  <DynamicYield />
                  <ReactHooksWrapper />
                  {!(process.env.NEXT_PUBLIC_APP_ENV === "prod") && <Preview />}
                  <ReturnsFlowProvider>
                    <OrderCancellationProvider>
                      {children}
                    </OrderCancellationProvider>
                  </ReturnsFlowProvider>
                </GlobalProvider>
              </NotificationsManager>
            </PageFromPathProvider>
          </GlobalErrorAlertProvider>
        </RootErrorBoundary>
      </BreakpointSizeProvider>
    </RouterEventProvider>
  );
};

/**
 * A `PageProvider` that uses the current path to generate a page object.
 * @throws If the pathname is `null` in pages router.
 * @see {@link https://nextjs.org/docs/app/api-reference/functions/use-pathname usePathname()}
 */
const PageFromPathProvider: FC<PropsWithChildren> = ({ children }) => {
  const path = usePathname();
  if (isNullish(path)) {
    // may return `null` in pages router
    throw new InvalidStateError('No path found in the router.');
  }

  // Memoize by path so the page reference only changes on route transitions.
  // Without this, every parent re-render would create a new page object,
  // causing PageContext consumers to re-render unnecessarily.
  const page = useMemo(() => PageService.generatePageFromPath(path), [path]);

  return <PageProvider page={page}>{children}</PageProvider>;
};

export default ClientRoot;
